// Growth › Campaigns.

import { Link } from 'react-router-dom'
import { api } from '@/api'
import type { Campaign } from '@/api/growthCampaignTypes'
import { PageHeader } from '@/components/shell/PageHeader'
import { ButtonLink, Card, DataTable, EmptyState, ErrorState, PlusIcon, RelativeTime, SkeletonRows, type Column } from '@/components/ui'
import { useDocumentTitle } from '@/lib/hooks'
import { useQuery } from '@/lib/query'
import { CampaignStatusBadge } from './campaignShared'

function Count({ value }: { value: number }) {
  return <span className="tabular text-muted">{value}</span>
}

const COLUMNS: Column<Campaign>[] = [
  {
    id: 'name',
    header: 'Campaign',
    primary: true,
    cell: (c) => (
      <span className="grid min-w-0 gap-0.5">
        <span className="break-words font-semibold">{c.name}</span>
        <span className="text-xs font-medium text-muted">
          {c.steps.length} {c.steps.length === 1 ? 'email' : 'emails'} · {c.daily_cap} a day
        </span>
      </span>
    ),
    className: 'min-w-[12rem]',
  },
  { id: 'status', header: 'Status', cell: (c) => <CampaignStatusBadge status={c.status} /> },
  { id: 'sent', header: 'Sent', align: 'right', cell: (c) => <Count value={c.stats.sent} /> },
  { id: 'dry', header: 'Dry run', align: 'right', cell: (c) => <Count value={c.stats.dry_run} /> },
  { id: 'clicked', header: 'Clicked', align: 'right', cell: (c) => <Count value={c.stats.clicked} /> },
  { id: 'replied', header: 'Replied', align: 'right', cell: (c) => <Count value={c.stats.replied} /> },
  { id: 'bounced', header: 'Bounced', align: 'right', cell: (c) => <Count value={c.stats.bounced} /> },
  { id: 'created', header: 'Created', hideOnMobile: true, cell: (c) => <RelativeTime value={c.created_at} className="text-muted" /> },
]

export default function CampaignsListPage() {
  useDocumentTitle('Campaigns')
  const { data, error, status, refetch } = useQuery(['growth', 'campaigns'], (signal) => api.growth.campaigns.list(signal))
  const rows = data ?? []

  return (
    <div className="grid grid-cols-1 gap-5">
      <PageHeader
        title="Campaigns"
        description={
          <>
            Outreach to leads, a few a day. Adding one to the schedule approves every email it plans; see each day on the <Link to="/growth/schedule" className="underline underline-offset-2 hover:text-accent">Schedule</Link>.
          </>
        }
        actions={
          <ButtonLink to="/growth/campaigns/new" variant="primary" icon={<PlusIcon size={15} />}>
            New campaign
          </ButtonLink>
        }
      />
      <Card flush className="overflow-hidden">
        {status === 'loading' ? (
          <SkeletonRows rows={4} label="Loading campaigns" />
        ) : status === 'error' && !data ? (
          <ErrorState error={error} title="Couldn't load campaigns" onRetry={refetch} />
        ) : rows.length === 0 ? (
          <EmptyState title="No campaigns yet" description="Pick who to write to, what to say and when." action={<ButtonLink to="/growth/campaigns/new">New campaign</ButtonLink>} />
        ) : (
          <DataTable caption="Campaigns" rows={rows} columns={COLUMNS} rowKey={(c) => c.id} rowHref={(c) => `/growth/campaigns/${c.id}`} />
        )}
      </Card>
    </div>
  )
}
